import type PlaySessionEventActionTypes from "../../shared/enums/PlaySessionEventActionTypes";
import { generateMockPlaySessionResults, type MockPlayItem, type RandomSource } from "./mock-play-session-results";

const COMPLETE_ACTION = "complete" as PlaySessionEventActionTypes.COMPLETE;

export const MOCKABLE_PLAY_SESSION_EVENTS = {
  StartPlaylist: "StartPlaylist",
  GFootball: "GFootball",
} as const;

interface BridgeEvent {
  type: string;
  payload?: {
    play_session_id?: unknown;
    items?: MockPlayItem[];
    data?: { playSessionId?: unknown; cognitiveResultId?: unknown };
  };
}

interface MockSessionContext {
  playSessionId: number;
  cognitiveResultId: number;
}

interface GameplayEvent {
  action: "enter" | "exit";
  module: "playcore" | "matchplay";
}

interface ControllerDependencies {
  dispatch: (action: {
    type: unknown;
    payload: ReturnType<typeof generateMockPlaySessionResults>[number] & { mock_play_session: true };
    disableLoaders: true;
    onSuccess: () => void;
    onError: (error: unknown) => void;
  }) => unknown;
  updateResultsAction: unknown;
  emitComplete: (event: { action: PlaySessionEventActionTypes.COMPLETE; play_session_id: number }) => void;
  emitGameplayEntered?: (event: GameplayEvent) => void;
  emitGameplayExited?: (event: GameplayEvent) => void;
  reportError?: (error: unknown, context: MockSessionContext) => void;
  random?: RandomSource;
  now?: () => number;
}

interface NormalizedLaunch {
  playSessionId: number;
  playItems: MockPlayItem[];
  gameplayModule: GameplayEvent["module"];
}

const validId = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value) && value > 0;

export const normalizeMockPlaySessionLaunch = (event: BridgeEvent): NormalizedLaunch | null => {
  if (event.type === MOCKABLE_PLAY_SESSION_EVENTS.StartPlaylist) {
    const playSessionId = event.payload?.play_session_id;
    const playItems = event.payload?.items;
    if (!validId(playSessionId) || !Array.isArray(playItems) || playItems.length === 0) return null;
    return { playSessionId, playItems, gameplayModule: "playcore" };
  }

  if (event.type === MOCKABLE_PLAY_SESSION_EVENTS.GFootball) {
    const data = event.payload?.data;
    if (!validId(data?.playSessionId) || !validId(data?.cognitiveResultId)) return null;
    return {
      playSessionId: data.playSessionId,
      playItems: [{ cognitive_result_id: data.cognitiveResultId }],
      gameplayModule: "matchplay",
    };
  }

  return null;
};

export const createMockPlaySessionController = ({
  dispatch,
  updateResultsAction,
  emitComplete,
  emitGameplayEntered = () => {},
  emitGameplayExited = () => {},
  reportError = (error, context) => console.error("Mock play session failed", context, error),
  random = Math.random,
  now = Date.now,
}: ControllerDependencies) => {
  const activeSessions = new Set<number>();
  const completedSessions = new Set<number>();

  const submitResult = (payload: ReturnType<typeof generateMockPlaySessionResults>[number], playSessionId: number) =>
    new Promise<void>((resolve, reject) => {
      const context = { playSessionId, cognitiveResultId: payload.cognitive_result_id };
      try {
        dispatch({
          type: updateResultsAction,
          payload: { ...payload, mock_play_session: true },
          disableLoaders: true,
          onSuccess: () => resolve(),
          onError: (error: unknown) => {
            reportError(error, context);
            reject(error);
          },
        });
      } catch (error) {
        reportError(error, context);
        reject(error);
      }
    });

  const run = async (launch: NormalizedLaunch, payloads: ReturnType<typeof generateMockPlaySessionResults>) => {
    let updatesSucceeded = false;
    try {
      for (const payload of payloads) await submitResult(payload, launch.playSessionId);
      updatesSucceeded = true;
    } catch {
      // submitResult reports the failing item with actionable context.
    }

    emitGameplayExited({ action: "exit", module: launch.gameplayModule });
    activeSessions.delete(launch.playSessionId);

    if (updatesSucceeded) {
      completedSessions.add(launch.playSessionId);
      emitComplete({ action: COMPLETE_ACTION, play_session_id: launch.playSessionId });
    }
  };

  const handle = (event: BridgeEvent): boolean => {
    const launch = normalizeMockPlaySessionLaunch(event);
    if (!launch) return false;
    if (activeSessions.has(launch.playSessionId) || completedSessions.has(launch.playSessionId)) return true;

    let payloads: ReturnType<typeof generateMockPlaySessionResults>;
    try {
      payloads = generateMockPlaySessionResults(launch.playItems, { random, now });
    } catch (error) {
      reportError(error, { playSessionId: launch.playSessionId, cognitiveResultId: 0 });
      return true;
    }

    activeSessions.add(launch.playSessionId);
    emitGameplayEntered({ action: "enter", module: launch.gameplayModule });
    void run(launch, payloads);
    return true;
  };

  return { handle };
};
